import { Box, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useNavigate, useParams } from "react-router-dom";

import APInfoText from "../../../components/APInfoText";
import APButton from "../../../components/buttons/APButton";
import APContactHelpButton from "../../../components/buttons/APContactHelpButton";
import BackButton from "../../../components/buttons/BackButton";
import HomeButton from "../../../components/buttons/HomeButton";
import InfoButton from "../../../components/buttons/InfoButton";
import LoadingBackdrop from "../../../components/LoadingBackdrop";
import PageContainer from "../../../components/PageContainer";
import RestrictedAppScope from "../../../components/RestrictedAppScope";
import { PersonaliseQuoteInputs } from "../../../helpers/quotes";
import { useFaqs } from "../../../hooks/useFaqs";
import { useQuotePersonalisation } from "../../../hooks/useQuotePersonalisation";
import { useVehicleSourcingAccess } from "../../../hooks/useVehicleSourcingAccess";
import { useAuth } from "../../../providers/AuthProvider";
import VehicleOptionsList from "../../vehicleDetail/components/VehicleOptionsList";

const pageTitle = "Manufacturer Options";

const titleButton = (
  <Box>
    <InfoButton />
  </Box>
);

type VehicleSourcingManufacturerOptionsWithQuoteProps = {
  quoteId: string;
  dossierId: string;
  opportunityId?: string;
  driverSourcedVehicle?: boolean;
};

const VehicleSourcingManufacturerOptionsWithQuote = ({
  quoteId,
  dossierId,
  opportunityId,
  driverSourcedVehicle,
}: VehicleSourcingManufacturerOptionsWithQuoteProps) => {
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();

  const {
    control,
    handleSubmit,
    formattedQuote,
    vehicleOptions,
    isLoading: isLoadingQuotePersonalisation,
    isSuccess: isSuccessQuotePersonalisation,
    isSubmitting,
    hasPricingRequests,
    submitOptions,
  } = useQuotePersonalisation({ quoteId, scope: "manufacturer" });

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "select-manufacturer-options-to-source",
    contextIds: {
      opportunityId: opportunityId,
    },
  });

  const onNext = async (values: PersonaliseQuoteInputs) => {
    await submitOptions(values);
    navigate(
      getScopedPageUrl("dossierVehicleSourcingConfirmConfiguration", dossierId)
    );
  };

  const isLoading = isLoadingFaqs || isLoadingQuotePersonalisation;
  const isSuccess = isSuccessFaqs && isSuccessQuotePersonalisation;

  const renderAppLocation = faqProps.renderAppLocation;

  return (
    <PageContainer
      loading={isLoading}
      title={pageTitle}
      titleButton={
        isSuccess ? (
          <Box mr={1}>
            {renderAppLocation(
              "SelectManufacturerOptionsToSource-Page",
              <InfoButton />,
              true,
              true
            )}
          </Box>
        ) : undefined
      }
    >
      {isSuccess && formattedQuote && vehicleOptions.data && (
        <Stack spacing={2}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="labelLarge">
              Select manufacturer options.
            </Typography>

            {!driverSourcedVehicle && (
              <Typography variant="labelLarge">
                Please ensure these are correct as they affect pricing and
                availability.
              </Typography>
            )}
          </Box>

          <Box>
            <VehicleOptionsList
              title="Manufacturer Options"
              options={Object.values(vehicleOptions.data.options.manufacturer)}
              defaultExpanded={true}
              quote={formattedQuote}
              control={control}
              disableCheckboxes={
                hasPricingRequests ||
                formattedQuote.isLocked ||
                formattedQuote.isVehicleConfigurationLocked
              }
              showTerms={true}
            />
          </Box>
          {!driverSourcedVehicle &&
            renderAppLocation(
              "SelectManufacturerOptionsToSource-SelectCorrectOptionsText",
              <APInfoText text="Please ensure you select the correct options as this will affect pricing and availability." />
            )}

          <Stack
            spacing={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              pt: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                columnGap: 1,
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Box
                  sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                >
                  <BackButton />
                  <HomeButton destination="dossiersHome" />
                </Box>
              </Box>
              <Box>
                <APButton
                  text="Next"
                  variant="contained"
                  color="secondary"
                  onClick={handleSubmit(onNext)}
                  icon="play"
                />
              </Box>
            </Box>
            <Box>
              <RestrictedAppScope scope="external">
                <APContactHelpButton regardingOpportunityId={opportunityId} />
              </RestrictedAppScope>
            </Box>
          </Stack>
          {isSubmitting && <LoadingBackdrop />}
        </Stack>
      )}
    </PageContainer>
  );
};

const VehicleSourcingManufacturerOptions = () => {
  const { dossierId } = useParams();

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const {
    dossier,
    navigationCheckComplete,
    isLoading: isDossierLoading,
  } = useVehicleSourcingAccess({ dossierId });

  const primaryQuoteId = dossier.data?.opportunity?.primaryQuoteId ?? null;
  const driverSourcedVehicle =
    dossier.data?.opportunity?.driverSourcedVehicle ?? false;

  if (isDossierLoading || !navigationCheckComplete) {
    return (
      <PageContainer loading title={pageTitle} titleButton={titleButton}>
        {null}
      </PageContainer>
    );
  }

  if (!primaryQuoteId) {
    return (
      <PageContainer
        title={pageTitle}
        titleButton={titleButton}
        loading={false}
      >
        {null}
      </PageContainer>
    );
  }

  return (
    <VehicleSourcingManufacturerOptionsWithQuote
      quoteId={primaryQuoteId}
      dossierId={dossierId}
      opportunityId={dossier.data?.opportunity?.id}
      driverSourcedVehicle={driverSourcedVehicle}
    />
  );
};

export default VehicleSourcingManufacturerOptions;
